import { getPipeSeriesQueryKey } from "@imi/api-client/index.js";
import { InsertIcon } from "@imi/icons/InsertIcon.js";
import { UndoIcon } from "@imi/icons/UndoIcon.js";
import { Button } from "@imi/ui/components/Button/Button.js";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogClose,
  DialogCloseButton,
  DialogHeader,
} from "@imi/ui/components/Dialog/Dialog.js";
import { Heading } from "@imi/ui/components/Heading/Heading.js";
import { selectSubmitOnEnter } from "@src/shared/helpers/form";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Form, useNavigate } from "react-router";
import { useRouteDialogPath } from "../../hooks/useRouteDialogPath";
import { useRouteDialogProps } from "../../hooks/useRouteDialogProps";
import { PipeFieldset } from "./components/PipeFieldset";
import { useCustomPipeSeriesForm } from "./hooks/useCustomPipeSeriesForm";
import styles from "./SettingsPipeSeriesAddDialog.module.css";

export function SettingsPipeSeriesAddDialog() {
  const { t } = useTranslation("settings");
  const routeDialogProps = useRouteDialogProps();
  const navigate = useNavigate();
  const { getRouteDialogPath } = useRouteDialogPath();
  const queryClient = useQueryClient();

  const { defaultValues, definitions, form, submitting } =
    useCustomPipeSeriesForm({
      onSubmit: (submission) => {
        queryClient.invalidateQueries({
          queryKey: getPipeSeriesQueryKey(),
        });
        navigate(
          getRouteDialogPath("settings.pipeSeries", {
            args: { defaultPipeSeries: submission.id },
          }),
        );
      },
    });

  return (
    <Dialog {...routeDialogProps}>
      <DialogHeader>
        <Heading as="h3" subTitle={t("pipeSeries.add.subTitle")}>
          {t("pipeSeries.add.title")}
        </Heading>
      </DialogHeader>
      <DialogBody className={styles.DialogBody} tabIndex={-1}>
        <Form
          className={styles.Form}
          id={form.formId}
          onKeyDown={(e) => selectSubmitOnEnter(e, submitting)}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit(e);
          }}
        >
          <fieldset className={styles.Fieldset}>
            <form.AppField name="name">
              {(field) => (
                <field.TextField
                  errors={field.state.meta.errors.map((error) =>
                    error ? t(error.message) : undefined,
                  )}
                  label={t("pipeSeries.add.fields.name.label")}
                  placeholder={t("pipeSeries.add.fields.name.placeholder")}
                />
              )}
            </form.AppField>
          </fieldset>

          <form.AppField mode="array" name="pipes">
            {(pipesField) => (
              <>
                {pipesField.state.value.map((_, index) => (
                  <PipeFieldset
                    className={styles.Fieldset}
                    definitions={definitions}
                    form={form}
                    index={index}
                    key={_.id}
                  />
                ))}
                <fieldset className={styles.AddPipe}>
                  <Button
                    onClick={() => {
                      pipesField.pushValue({
                        ...defaultValues.pipes[0],
                        id: crypto.randomUUID(),
                      });
                    }}
                    size="sm"
                    variant="outline"
                  >
                    <InsertIcon />
                    {t("pipeSeries.add.actions.addPipe")}
                  </Button>
                </fieldset>
              </>
            )}
          </form.AppField>
        </Form>
      </DialogBody>
      <DialogActions className={styles.DialogActions}>
        <Button
          className={styles.ResetButton}
          disabled={submitting}
          onClick={() => {
            form.reset();
          }}
          variant="outline"
        >
          <UndoIcon />
          {t("pipeSeries.add.actions.reset")}
        </Button>
        <DialogClose disabled={submitting} variant="outline">
          {t("pipeSeries.add.actions.cancel")}
        </DialogClose>
        <form.Button form={form.formId} loading={submitting} type="submit">
          {t("pipeSeries.add.actions.confirm")}
        </form.Button>
      </DialogActions>
      <DialogCloseButton>{t("pipeSeries.add.actions.close")}</DialogCloseButton>
    </Dialog>
  );
}
